/*
 * This file includes classes and functions which are relevant for the pluralistic-view feature.
 * It is used by both the server and the client.
*/
import type { Post } from "~~/prisma/generated/client"

class RateLimit {
  constructor(initial, k, burst) {
    this.initial = initial
    this.k = k
    this.burst = burst
    this.buckets = {}
  }

  add() {
    for (const s in this.buckets) {
      this.buckets[s] += this.k
      if (this.buckets[s] > this.burst) {
        this.buckets[s] = this.burst
      }
    }
  }

  getBucket(s) {
    if (this.buckets[s] === undefined) {
      this.buckets[s] = this.initial
    }
    return this.buckets[s]
  }

  check(s) {
    return this.getBucket(s) >= 1
  }

  pop(s) {
    if (this.check(s)) {
      this.buckets[s] -= 1
      return true
    }
    return false
  }
}

export class Shuffler {
  readonly postsQueue: Post[];

  constructor(rateLimiter: RateLimit, reserve: number) {
    // a reserve is useful to make the fairness more effective
    // in fact, with reserve=0, every batch of 12 posts is reshuffled on its own,
    // but no post will go to the next batch.
    // With a larger reserve, this can happen.
    this.rateLimiter = rateLimiter
    this.reserve = reserve
    this.removed = []
    this.postsQueue = []
  }

  add(listOfPosts) {
    if (listOfPosts === undefined) {
      console.error('Shuffler.add(undefined)')
      return
    }
    if (listOfPosts.value !== undefined) {
      listOfPosts = listOfPosts.value
    }
    for (const item of listOfPosts) {
      if (!this.removed.includes(item.id)
        && this.postsQueue.findIndex(p => p.id == item.id) == -1
      ) {
        this.postsQueue.push(item)
      }
    }
  }

  remove(post) {
    this.removed.push(post.id)
    const idx = this.postsQueue.findIndex(p => p.id === post.id)
    if (idx !== -1) {
      this.postsQueue.splice(idx, 1)
    }
    else {
      console.error('Trying to remove a post which has never been added?')
    }
  }

  addAndGet(listOfPosts) {
    this.add(listOfPosts)
    const newPosts = []

    while (this.postsQueue.length > this.reserve && newPosts.length < listOfPosts.length) {
      let idx = 0
      for (const item of this.postsQueue) {
        const source = item.source.id
        if (this.rateLimiter.pop(source)) {
          newPosts.push(item)
          this.remove(item)
          break
        }
        else {
        }
        idx += 1
      }
      this.rateLimiter.add()
    }
    return newPosts
  }
}

export function make_shuffler(): Shuffler {
  return new Shuffler(
    // on average, one post every five can be from a source. But burst of 3 posts
    // in a row can happen.
    new RateLimit(1, 0.2, 2.6),
    // keep a reserve of posts which are not displayed, waiting for next batch
    8)
}
